import React, { useState } from 'react';
import { MapPin, Phone, Clock, ExternalLink, ChevronDown, Eye } from 'lucide-react';
import { branches } from '../data/mock';

const Locations = () => {
  const [activeBranch, setActiveBranch] = useState(branches[0].id);

  const selectedBranch = branches.find((b) => b.id === activeBranch);

  return (
    <section id="locations" className="py-24 bg-stone-900 relative">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-amber-500 mb-4">
            <div className="w-12 h-px bg-amber-600" />
            <MapPin className="w-6 h-6" />
            <div className="w-12 h-px bg-amber-600" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-amber-50 mb-4">
            Şubelerimiz
          </h2>
          <p className="text-amber-200/60 max-w-xl mx-auto">
            Balıkesir'de iki farklı lokasyonumuzda sizi ağırlamaktan mutluluk duyarız
          </p>
        </div>

        {/* Branch Selector */}
        <div className="flex justify-center gap-4 mb-12">
          {branches.map((branch) => (
            <button
              key={branch.id}
              onClick={() => setActiveBranch(branch.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeBranch === branch.id
                  ? 'bg-amber-600 text-stone-950 shadow-lg shadow-amber-600/30'
                  : 'bg-stone-800/50 text-amber-200/80 hover:bg-stone-800 border border-amber-900/30'
              }`}
            >
              {branch.name}
              {branch.isMain && (
                <span className="ml-2 text-xs opacity-70">(Ana)</span>
              )}
            </button>
          ))}
        </div>

        {/* Branch Details */}
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Info Card */}
            <div className="bg-stone-950/50 rounded-2xl p-8 border border-amber-900/20">
              <h3 className="font-serif text-2xl text-amber-50 mb-6">
                {selectedBranch?.name}
              </h3>

              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <div className="text-amber-200/50 text-sm mb-1">Adres</div>
                    <div className="text-amber-100">{selectedBranch?.address}</div>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <div className="text-amber-200/50 text-sm mb-1">Telefon</div>
                    <a
                      href={`tel:${selectedBranch?.phone}`}
                      className="text-amber-100 hover:text-amber-400 transition-colors"
                    >
                      {selectedBranch?.phone}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <div className="text-amber-200/50 text-sm mb-1">Çalışma Saatleri</div>
                    <div className="text-amber-100">{selectedBranch?.hours}</div>
                  </div>
                </div>
              </div>

              {/* Map Link */}
              <div className="mt-8 flex flex-col gap-3">
                <a
                  href={selectedBranch?.virtualTourUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-500 text-stone-950 font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/30"
                >
                  <Eye className="w-5 h-5" />
                  360° Sanal Tur ile Gez
                </a>
                <a
                  href={selectedBranch?.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-amber-600/50 text-amber-100 hover:bg-amber-600/20 hover:border-amber-500 font-medium rounded-lg transition-all duration-300"
                >
                  <MapPin className="w-5 h-5" />
                  Haritada Gör
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-stone-950/50 rounded-2xl overflow-hidden border border-amber-900/20 min-h-[400px] relative">
              <iframe
                src={`https://maps.google.com/maps?q=${encodeURIComponent(selectedBranch?.address || '')}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                className="w-full h-full absolute inset-0"
                style={{ border: 0, filter: 'grayscale(40%) contrast(1.1)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Konum"
              />
            </div>
          </div>
        </div>

        {/* All Branches Overview */}
        <div className="mt-16 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {branches.map((branch) => (
            <div
              key={branch.id}
              onClick={() => setActiveBranch(branch.id)}
              className={`p-6 rounded-xl cursor-pointer transition-all duration-300 ${
                activeBranch === branch.id
                  ? 'bg-amber-600/10 border-2 border-amber-600/50'
                  : 'bg-stone-950/30 border border-amber-900/20 hover:border-amber-600/30'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-serif text-lg text-amber-50 mb-1">
                    {branch.name}
                  </h4>
                  <p className="text-amber-200/50 text-sm">{branch.address}</p>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-amber-500 transition-transform ${
                    activeBranch === branch.id ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Locations;